import React from 'react';
import { Save, Loader2, Cloud, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

/**
 * AWS SES configuration form — pure presentational. Sends via boto3
 * SendRawEmail; the From email must be a verified SES identity.
 */
export function SesForm({
  form,         // { accessKey, secret, showSecret, region, fromEmail, fromName }
  set,          // (key, value) => void
  ready,
  saving,
  onSubmit,
}) {
  return (
    <form
      onSubmit={onSubmit}
      data-testid="ses-form"
      className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-5"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
          <Cloud className="w-5 h-5 text-orange-400" />
        </div>
        <div>
          <div className="text-white font-semibold flex items-center gap-2">
            AWS SES (API)
            {ready && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                <CheckCircle2 className="inline w-2.5 h-2.5 mr-1" /> Configured
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400">
            Sends via boto3 <span className="font-mono">SendRawEmail</span>. From-email must be a verified SES identity.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="AWS Access Key ID">
          <Input value={form.accessKey} onChange={(e) => set('accessKey', e.target.value)} placeholder="AKIAxxxxxxxxxxxxxxxx" data-testid="ses-access-key"
            className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm" />
        </Field>
        <Field label="AWS Secret Access Key">
          <div className="relative">
            <Input
              type={form.showSecret ? 'text' : 'password'}
              value={form.secret}
              onChange={(e) => set('secret', e.target.value)}
              placeholder={ready ? '•••••••• (leave blank to keep)' : 'AWS secret access key'}
              data-testid="ses-secret"
              className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm pr-10"
            />
            <button
              type="button"
              onClick={() => set('showSecret', !form.showSecret)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              tabIndex={-1}
            >
              {form.showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>
        <Field label="AWS Region">
          <Input value={form.region} onChange={(e) => set('region', e.target.value)} placeholder="us-east-1" data-testid="ses-region"
            className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm" />
        </Field>
        <Field label="From email (verified)">
          <Input value={form.fromEmail} onChange={(e) => set('fromEmail', e.target.value)} placeholder="noreply@yourdomain.com" data-testid="ses-from-email"
            className="bg-slate-800/50 border-slate-700 text-white text-sm" />
        </Field>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">From name</Label>
          <Input value={form.fromName} onChange={(e) => set('fromName', e.target.value)} placeholder="Your Brand" data-testid="ses-from-name"
            className="bg-slate-800/50 border-slate-700 text-white text-sm" />
        </div>
      </div>

      <div className="rounded-lg bg-orange-500/5 border border-orange-500/20 p-3 text-xs text-orange-200/90">
        <strong>IAM permission required:</strong> <span className="font-mono">ses:SendRawEmail</span> on your verified identity. New AWS accounts start in SES sandbox mode — request production access before sending to unverified recipients.
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} data-testid="ses-save"
          className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save SES configuration</>}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

export default SesForm;
